import React, { useMemo } from 'react'
import log from 'loglevel'
import { Flags, FlagsProvider } from 'react-feature-flags'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  FeatureAccessRequest,
  FeatureAccessResponse,
  FEATURE_ACCESS,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useUser } from 'src/components/async/Users'
import { usePlaybooks } from 'src/pages/TrusteroIntelligence/playbooks/playbooks.hooks'
import { isNewerVersion } from 'src/Utils/semver'
import { loadFromStorage } from '../Utils/storage'
import { EnvConfig } from '../config/config'
import {
  isDeniedInternalEmail,
  useIsInternalUser,
  useIsProvider,
} from './authContext'

const logger = log.getLogger('context')

export enum GatekeeperFlag {
  REGISTER_PAGES = 'registerPages',
  RELEVANT_DATE = 'relevantDate',
  MULTI_FRAMEWORK = 'multiFramework',
  CUSTOM_RECEPTOR = 'customReceptor',
  GRID_COLUMN_SORT = 'gridColumnSort',
  SUGGESTED_AUTOMATED_EVIDENCE = 'suggestedAutomatedEvidence',
  MULTI_FRAMEWORK_SUGGESTIONS = 'multiFrameworkSuggestions',
  STUB_FRAMEWORKS = 'stubFrameworks',
  REPORT_SCAN = 'reportScan',
  SUGGESTED_DLR_EVIDENCE = 'suggestedDLREvidence',
  CRITERIA_CONTROL_MAPPING = 'criteriaControlMapping',
  CONTROL_IDS = 'controlIds',
  RISK_REGISTER = 'riskRegister',
  STANDALONE_SCANNER = 'standaloneScanner',
  SNYK_RECEPTOR = 'snykReceptor',
  AUDIT_BOT = 'auditBot',
  SERVICES_OVERHAUL = 'servicesOverhaul',
  MIRADORE_RECEPTOR = 'miradoreReceptor',
  SOPHOS_RECEPTOR = 'sophosReceptor',
  VENDOR_MANAGEMENT = 'vendorManagement',
  JUMPCLOUD_RECEPTOR = 'jumpcloudReceptor',
  MS_LOGIN = 'msLogin',
  KNOWBE4_RECEPTOR = 'knowbe4Receptor',
  MULTI_FRAMEWORK_AUDITBOT = 'multiFrameworkAuditbot',
  SERVICE_ACCOUNTS = 'serviceAccounts',
  CC_ROADMAP = 'ccRoadmap',
  CONTINUOUS_MONITORING = 'continuousMonitoring',
  GOOGLE_DRIVE_AUTH = 'googleDriveAuth',
  JIRA_RECEPTOR = 'jiraReceptor',
  GOOGLE_DRIVE_POLICY_IMPORT = 'googleDrivePolicyImport',
  POLICY_RESPONSIBILITY = 'policyResponsibility',
  NEW_EVIDENCE_TABLE = 'newEvidenceTable',
  CONTROL_EVIDENCE_NEW_TAB = 'controlEvidenceNewTab',
  GENERATE_SYSTEM_DESCRIPTION = 'generateSystemDescription',
  RELEVANT_DATE_FORM = 'relevantDateForm',
  EVIDENCE_INDEX = 'evidenceIndex',
  SLACK_RECEPTOR = 'slackReceptor',
  SONARQUBE_CLOUD_RECEPTOR = 'sonarqubeCloudReceptor',
  FRESHDESK_RECEPTOR = 'freshdeskReceptor',
  JSM_RECEPTOR = 'jsmReceptor',
  CONFLUENCE_RECEPTOR = 'confluenceReceptor',
  ZENDESK_RECEPTOR = 'zendeskReceptor',
  PINGONE_LOGIN = 'pingOneLogin',
  CITE_SOURCES = 'citeSources',
  HUBSPOT_RECEPTOR = 'hubspotReceptor',
  OKTA_RECEPTOR = 'oktaReceptor',
  TEST_TABLE = 'testTable',
  AZUREBOARDS_RECEPTOR = 'azureBoardsReceptor',
  USE_AI_QUESTION_USAGE_TYPE = 'useAiQuestionUsageType',
  FLAT_HELP_PAGE = 'flatHelpPage',
  RIPPLING_RECEPTOR = 'ripplingReceptor',
  TRUST_PORTAL = 'trustPortal',
  USE_INDEXING_STATUS_PAGE = 'useIndexingStatusPage',
  JIT_RECEPTOR = 'jitReceptor',
  SHAREPOINT = 'sharepoint',
  LEVEL_OF_DETAIL = 'levelOfDetail',
  RISK_CATEGORY_CONFIGURATION = 'riskCategoryConfiguration',
  JIRA_WORKFLOW = 'jiraWorkflow',
  SERVICENOW_WORKFLOW = 'servicenowWorkflow',
  GRC_DASHBOARDS = 'grcDashboards',
  NEW_TRUST_PORTAL = 'newTrustPortal',
  RISK_RESPONSE_DASHBOARD = 'riskResponseDashboard',
  DOCEBO_RECEPTOR = 'doceboReceptor',
  SALESFORCE_RECEPTOR = 'salesforceReceptor',
  ONSPRING_RECEPTOR = 'onspringReceptor',
  SMARTSUITE_RECEPTOR = 'smartsuiteReceptor',
  CONFLUENCE_DMS = 'confluenceDMS',
  VIEW_LINKER_DOCUMENTS = 'viewLinkerDocuments',
  USE_SCHEDULING = 'useScheduling',
  CONTROL_EVIDENCE_MAPPINGS = 'controlEvidenceMappings',
  NEW_EVIDENCE_PAGE = 'newEvidencePage',
  MULTI_POLICY_CONTROL_LINKAGES = 'multiPolicyControlLinkages',
  NEW_LOGIN_PAGE = 'newLoginPage',
  NEW_POLICY_PAGE = 'newPolicyPage',
  POLICY_FOLDER_SCAN = 'policyFolderScan',
  CUSTOM_CONTROL_STATUSES = 'customControlStatuses',
  CUSTOM_RISK_STATUSES = 'customRiskStatuses',
  CONTROL_STATUS_COLOR_PICKER = 'controlStatusColorPicker',
  SHOW_EDITED_TEST_DETAILS = 'showEditedTestDetails',
  INCIDENTIO = 'incidentioReceptor',
  CROWDSTRIKE_RECEPTOR = 'crowdstrikeReceptor',
  RAPID7_RECEPTOR = 'rapid7Receptor',
  NEW_REQUESTS = 'newRequests',
  LABELS = 'labels',
  OVERVIEW_DASHBOARD = 'overviewDashboard',
  OVERVIEW_DASHBOARD_DRAGGABLE = 'overviewDashboardDraggable',
  FRAMEWORK_REQUIREMENTS_TAB = 'frameworkRequirementsTab',
  CONTENT_UPGRADE = 'contentUpgrade',
  ALLOW_PLG_SIGNUP = 'allowPlgSignup',
  SOC2_TRUST_SERVICE_CRITERIA = 'soc2TrustServiceCriteria',
  REACT_PLAYBOOKS = 'ReActPlaybooks',
  REQUEST_TI_ANSWER = 'requestTiAnswer',
  // Routes the CSV controls upload through the new import-wizard staging path
  // (StageImportControls) instead of the legacy ImportControls path.
  CONTROL_IMPORT_WIZARD = 'controlImportWizard',
  ACTIVITIES_PAGE = 'activitiesPage',
  LABEL_RBAC = 'labelRbac',
  GRC_IMPORT_WIZARD = 'grcImportWizard',
  INTERNAL_ACCOUNT_MANAGEMENT = 'internalAccountManagement',
  TPRM_QUESTIONNAIRE = 'tprmQuestionnaire',
  // The OUTBOUND half of TPRM_QUESTIONNAIRE: questionnaires we send to
  // vendors, and the vendor-side surfaces that answer them. Held back so the
  // inbound half -- Customer Questionnaires, which ships first -- is not
  // waiting on it. Positive rather than a `hideVendor...`: a flag missing from
  // one of the four config.json files reads falsy, and falsy has to mean
  // HIDDEN in production, which an inverted flag would get backwards.
  VENDOR_TPRM_QUESTIONNAIRE = 'vendorTprmQuestionnaire',
  SLIDING_EARLIEST_EVIDENCE = 'slidingEarliestEvidence',
  NEW_USER_ROLES_UI = 'newUserRolesUi',
  IF_GRANTED_PERMISSION_RULE = 'ifGrantedPermissionRule',
  // Mirrors ntrced's control_test_import flag; the import RPCs reject the call when it is off.
  CONTROL_TEST_IMPORT = 'controlTestImport',
  // Forces the API Tokens nav item on regardless of receptor state, for
  // exercising the external REST API (e.g. trr-smartsuite) in dev/testing
  // before an account has any GRC receptor enabled. Off in every prod env.
  API_TOKENS_PAGE = 'apiTokensPage',
  CONTROL_TEST_DOCX_EXPORT = 'controlTestDocxExport',
  SHORTCUT_RECEPTOR = 'shortcutReceptor',
  NOTION_RECEPTOR = 'notionReceptor',
  // Publish and Upgrade on the vendor evaluation guidance tab.
  GUIDANCE_PUBLISHING = 'guidancePublishing',
  // Due-date reminder settings on requests and sent vendor questionnaires.
  DUE_DATE_REMINDERS = 'dueDateReminders',
  // Receptors index "Add Receptor" button opens the full-page multi-select
  // Add Receptor modal instead of the receptor/folder scan dropdown.
  NEW_RECEPTOR_UI = 'newReceptorUi',
}

export type FlagOverride = {
  enabled: boolean
  minAppVersion: string
}

/**
 * Feature flag overrides fetched from ntrced for this environment.
 *
 * Module-level rather than React state on purpose: IsGatekeeperEnabled is a
 * plain synchronous function called from 300+ sites, many outside a component,
 * so the resolved values have to be readable without a hook. FeatureFlagProvider
 * fills this once per page load, before rendering anything that reads it.
 */
let featureFlagOverrides: Record<string, FlagOverride> = {}

export const setFeatureFlagOverrides = (
  overrides: Record<string, FlagOverride>,
): void => {
  featureFlagOverrides = overrides
}

export const getFeatureFlagOverrides = (): Record<string, FlagOverride> =>
  featureFlagOverrides

/**
 * The stored override for a flag, or null when none applies.
 *
 * minAppVersion suppresses *enabling* only. An override that turns a flag on is
 * ignored by a client older than minAppVersion, so a newly shipped feature can't
 * light up in a stale cached bundle that lacks the code. An override that turns
 * a flag off always applies: that's the kill switch, and an old client running a
 * broken feature is exactly who it needs to reach.
 */
const dbOverrideValue = (flag: string): boolean | null => {
  const override = featureFlagOverrides[flag]
  if (!override) {
    return null
  }
  if (
    override.enabled &&
    override.minAppVersion &&
    isNewerVersion(
      override.minAppVersion,
      process.env.REACT_APP_SEMVER || '0.0.0',
    )
  ) {
    return null
  }
  return override.enabled
}

function isLocallyEnabled(flag: string): boolean | null {
  // for our internal use, we want to allow users to override the value
  // of feature flags by setting keys in localStorage
  // Keys will be named "<env>_feature_<flag>"
  // The default value of `null` allows us to tell the caller
  // that the value was not found in localStorage
  const value = loadFromStorage(`feature_${flag}`, null)
  return value
}

export const GatekeeperProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  // verify that all defined flags are in our enum
  const flagData =
    (EnvConfig.featureFlags as { [key in GatekeeperFlag]: boolean }) || {}
  const allowedFlags = Object.values(GatekeeperFlag)
  for (const flag of Object.keys(flagData) as GatekeeperFlag[]) {
    if (!allowedFlags.includes(flag)) {
      logger.error(`Unknown gatekeeper flag in config file: ${flag}`)
    }
  }
  // delegate to FlagsProvider
  const featureFlags = Object.entries(EnvConfig.featureFlags || {}).map(
    ([flag, isEnvEnabled]) => {
      return {
        name: flag,
        isActive: isLocallyEnabled(flag) ?? isEnvEnabled,
      }
    },
  )
  return <FlagsProvider value={featureFlags}>{children}</FlagsProvider>
}

/*
 * This function is used to determine if a feature flag is enabled.
 * @param flag - The gatekeeper flag(s) to check - can be a single flag or an array of flags
 * @returns true if all of the flags are enabled, false otherwise
 */

/**
 * The flags gating the questionnaire feature, both halves of it.
 *
 * They exist to stage the CUSTOMER-side rollout: TPRM_QUESTIONNAIRE for
 * Customer Questionnaires, VENDOR_TPRM_QUESTIONNAIRE for the Vendors index and
 * the surfaces that answer what we sent out.
 */
const TPRM_FLAGS: GatekeeperFlag[] = [
  GatekeeperFlag.TPRM_QUESTIONNAIRE,
  GatekeeperFlag.VENDOR_TPRM_QUESTIONNAIRE,
]

// Must stay in sync with App.tsx's VENDOR_PORTAL_ROUTE_PATTERN, including its
// legacy account-less shape -- old invite emails still link to it. Read off
// window rather than useLocation because IsGatekeeperEnabled is a plain
// synchronous function called from outside React, and the portal is chosen
// purely by URL anyway (see App.tsx).
const VENDOR_PORTAL_PATH = /^\/(?:[^/]+\/)?vendor-portal(\/|$)/

/**
 * TPRM flags are ON in the vendor portal, whatever this environment's config
 * says -- unless an override explicitly turns them off.
 *
 * A vendor is only ever in the portal because a customer sent them a
 * questionnaire, which could only happen with the feature enabled on that
 * customer's side. The portal has no other purpose, so gating it on the
 * customer-side rollout flag turns an invite email into a dead link.
 *
 * Overrides are GLOBAL, not per-account (ListFeatureFlagOverrides takes no
 * account id), so no flag can express "on for the customer who invited you" --
 * which is the only question worth asking here.
 *
 * The concrete hazard this closes: the questionnaire components are SHARED
 * between the portal and Customer Questionnaires. The moment a shared component
 * gates on a TPRM flag, the portal inherits a gate meant for the customer's
 * staged rollout and goes dark for vendors, in a build where nothing on the
 * portal changed.
 *
 * Returns null rather than false when it does not apply, so it slots into the
 * resolution chain without shadowing the layers below it -- and sits BELOW the
 * override lookup, so the kill switch still reaches the portal.
 */
const vendorPortalDefault = (flag: GatekeeperFlag): boolean | null => {
  if (!TPRM_FLAGS.includes(flag)) {
    return null
  }
  return VENDOR_PORTAL_PATH.test(window.location.pathname) ? true : null
}

export const IsGatekeeperEnabled = (
  flag: GatekeeperFlag | GatekeeperFlag[],
): boolean => {
  // We only want to return true if all flags are enabled
  if (Array.isArray(flag)) {
    return !flag.some((f) => !IsGatekeeperEnabled(f))
  }

  const isEnvEnabled = EnvConfig?.featureFlags?.[flag] === true

  return (
    isLocallyEnabled(flag) ??
    dbOverrideValue(flag) ??
    vendorPortalDefault(flag) ??
    isEnvEnabled
  )
}

type GatekeeperProps = {
  authorizedFlags: GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
  renderOff?: () => JSX.Element | null
  renderOn?: () => JSX.Element | null
}

export const Gatekeeper = ({
  authorizedFlags,
  renderOff = () => null,
  renderOn = () => null,
  children,
}: GatekeeperProps): JSX.Element => {
  if (authorizedFlags.some((f) => !Object.values(GatekeeperFlag).includes(f))) {
    logger.error('Invalid Gatekeeper flag was passed in')
    return <></>
  }

  return (
    <Flags
      renderOff={renderOff}
      renderOn={renderOn}
      authorizedFlags={authorizedFlags}
    >
      {children}
    </Flags>
  )
}

type ReverseGatekeeperComponentProps = {
  shouldHide: <t>(arg: t) => boolean
  shouldHideArg: unknown
  authorizedFlags: GatekeeperFlag | GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
}

/**
 * This component is used to hide a component if the gatekeeper flag is NOT enabled and the shouldHide function returns true
 * @param shouldHide - a function that takes in a generic argument and returns a boolean
 * @param shouldHideArg - the argument to pass to the shouldHide function
 * @param authorizedFlags - the gatekeeper flag(s) to check
 * @param children - the component to render if the gatekeeper flag is enabled or the shouldHide function returns false
 * @returns the null if the gatekeeper flag is NOT enabled and the shouldHide function returns true, otherwise it returns the children
 */
export const ReverseGatekeeperComponent = ({
  shouldHide,
  shouldHideArg,
  authorizedFlags,
  children,
}: ReverseGatekeeperComponentProps): JSX.Element => {
  const gatekeeper = IsGatekeeperEnabled(authorizedFlags)
  return !gatekeeper && shouldHide(shouldHideArg) ? <></> : <>{children}</>
}

/**
 * Determine feature access from the backend
 */
export const useHasFeatureAccess = (
  featureType: FEATURE_ACCESS,
  shouldFetch = true,
): GrpcResponse<FeatureAccessResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.checkFeatureAccess,
    new FeatureAccessRequest().setFeatureType(featureType),
    shouldFetch && featureType > -1, // Ensure we don't call the API if the feature type is not valid (begins with 0 enum)
  )

  return NewGrpcResponse(response)
}

export const useHasGoogleDriveAuth = (): boolean => {
  const hasEnvAccess = IsGatekeeperEnabled(GatekeeperFlag.GOOGLE_DRIVE_AUTH)

  return hasEnvAccess
}

export const hasControlStatus = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.CONTINUOUS_MONITORING)

export const useHasGenerateSystemDescription = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.GENERATE_SYSTEM_DESCRIPTION)

export const useHasAnecdotesReceptor =
  (): GrpcResponse<FeatureAccessResponse> => {
    return useHasFeatureAccess(FEATURE_ACCESS.ANECDOTES_EVIDENCE_INDEX)
  }

export const useHasSlackNotification = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.SLACK_NOTIFICATION)
  return useMemo(() => data?.getAllowAccess() ?? false, [data])
}

export const useHasTeamsNotification = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.TEAMS_NOTIFICATION)
  return useMemo(() => data?.getAllowAccess() ?? false, [data])
}

export const useHasTrusteroIntelligence = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.TRUSTERO_INTELLIGENCE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasRiskCategoryConfiguration = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.RISK_CATEGORY_CONFIGURATION)
}

export const useHasTrustPortalConfiguration = (): boolean => {
  const isProvider = useIsProvider()
  // Product availability is independent of both page and data permissions.
  // Navigation applies the page capability; data calls apply their view rules.
  return isProvider && IsGatekeeperEnabled(GatekeeperFlag.TRUST_PORTAL)
}

export const useFlatHelpPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.FLAT_HELP_PAGE)

export const useIndexingStatusPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.USE_INDEXING_STATUS_PAGE)

export const useSharepointLinkPaste = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.USE_SHAREPOINT_LINK_PASTE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useRipplingReceptor = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.RIPPLING_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasGRCContentCleanup = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GRC_CONTENT_CLEANUP)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasContentPublishing = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.CONTENT_PUBLISHING)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

/**
 * True when this account is provisioned as a reporting parent, i.e. it may view aggregated
 * Operating Effectiveness results from its linked child accounts.
 *
 * Account-level entitlement only — on its own it does not authorize showing child data.
 * Surfacing that additionally requires a per-user permission check and at least one
 * consented, reporting child, so compose this rather than gating on it alone.
 */
export const useHasChildAccountReporting = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.CHILD_ACCOUNT_REPORTING)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useIsGlobalContentAccount = (): boolean => {
  const { data: currentUser } = useUser()
  const { data: playbooks } = usePlaybooks({ includeGlobalPlaybooks: true })

  return useMemo(() => {
    if (!currentUser || !playbooks) return false
    const currentAccountId = currentUser.getAccountid()
    return playbooks
      .getPlaybooksList()
      .some((p) => p.getIsGlobal() && p.getAccountId() === currentAccountId)
  }, [currentUser, playbooks])
}

export const useHasGRCDashboards = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.GRC_DASHBOARDS)
  const { data: user } = useUser()
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.GRC_DASHBOARDS,
  )
  const isTrustedUser = useMemo(() => {
    const email = user?.getEmail()
    return (
      (email?.toLowerCase().endsWith('@trustero.com') &&
        !isDeniedInternalEmail(email)) ||
      false
    )
  }, [user])
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || isTrustedUser || hasDashboards
}

export const useHasRiskDashboard = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.RISK_RESPONSE_DASHBOARD,
  )
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.RISK_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasDashboards
}

export const useOverviewDashboardAccess = (): {
  hasAccess: boolean
  loading: boolean
} => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD)
  const isInternalUser = useIsInternalUser()
  const { data: featureAccess, loading } = useHasFeatureAccess(
    FEATURE_ACCESS.OVERVIEW_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  const hasAccess = isFlagEnabled || isInternalUser || hasDashboards
  return {
    hasAccess,
    loading: !hasAccess && loading,
  }
}

export const useHasOverviewDashboard = (): boolean => {
  const { hasAccess } = useOverviewDashboardAccess()
  return hasAccess
}

export const useHasOverviewDashboardDraggable = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD_DRAGGABLE)

export const useHasScheduling = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.USE_SCHEDULING)
  const { data: featureAccess } = useHasFeatureAccess(FEATURE_ACCESS.SCHEDULING)
  const hasAccess = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasAccess
}

export const useHasNewEvidencePage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_EVIDENCE_PAGE)
}

export const useHasNewReceptorUi = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_RECEPTOR_UI)
}

export const useHasNewPolicyPage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_POLICY_PAGE)
}

export const useHasGenerateControls = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GENERATE_CONTROLS)
  return data?.getAllowAccess() || false
}

export const useHasCustomControlStatuses = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CUSTOM_CONTROL_STATUSES)
}

export const useHasCustomRiskStatuses = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CUSTOM_RISK_STATUSES)
}

export const useHasReActPlaybooks = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.REACT_PLAYBOOKS)

export const useHasTprmQuestionnaire = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.TPRM_QUESTIONNAIRE)
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.INBOUND_QUESTIONNAIRE_V2)
  return isFlagEnabled || !!data?.getAllowAccess()
}

/**
 * The outbound half: questionnaires we send to vendors, and what answers them.
 *
 * Enabled by its account grant, or questionnaire access plus the vendor flag.
 */
export const useHasVendorTprmQuestionnaire = (): boolean => {
  const hasTprmQuestionnaire = useHasTprmQuestionnaire()
  const hasVendorTprmQuestionnaire = IsGatekeeperEnabled(
    GatekeeperFlag.VENDOR_TPRM_QUESTIONNAIRE,
  )
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.OUTBOUND_QUESTIONNAIRE_V2)
  return (
    (hasTprmQuestionnaire && hasVendorTprmQuestionnaire) ||
    !!data?.getAllowAccess()
  )
}

/**
 * Resolves every playbook feature_flag string (stored on the Playbook proto) to
 * whether this account may see playbooks carrying it.
 *
 * A hook rather than a Record of hooks: the hooks have to be called
 * unconditionally and in a stable order, which iterating a map cannot promise.
 * This replaced a `Record<string, () => boolean>` that nothing ever consumed --
 * the real filtering lived in a hand-maintained duplicate inside
 * CopilotLibraryView, so the exported map drifted out of use while every other
 * playbook surface filtered nothing at all.
 *
 * Add a line here when a migration starts stamping a new feature_flag value.
 */
export const useResolvedPlaybookFeatureFlags = (): Record<string, boolean> => {
  const hasReActPlaybooks = useHasReActPlaybooks()
  // Trustero-employee-only playbooks: visible only to @trustero.com users.
  const isInternalUser = useIsInternalUser()
  // Playbooks for questionnaires this account receives (answer, import,
  // export): gated with the inbound questionnaire surfaces they write into.
  const hasTprmQuestionnaire = useHasTprmQuestionnaire()
  // Vendor playbooks (pre-fill, evaluate answers, tier, risk links): gated with
  // the questionnaires this account sends vendors.
  const hasVendorTprmQuestionnaire = useHasVendorTprmQuestionnaire()

  return useMemo(
    () => ({
      ReAct_playbooks: hasReActPlaybooks,
      trustero_internal: isInternalUser,
      tprm_questionnaire: hasTprmQuestionnaire,
      vendor_tprm_questionnaire: hasVendorTprmQuestionnaire,
    }),
    [
      hasReActPlaybooks,
      isInternalUser,
      hasTprmQuestionnaire,
      hasVendorTprmQuestionnaire,
    ],
  )
}

export const useHasControlStatusColorPicker = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CONTROL_STATUS_COLOR_PICKER)
}

export const useHasInternalAccountManagement = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.INTERNAL_ACCOUNT_MANAGEMENT)
}
export const useHasActivitiesPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.ACTIVITIES_PAGE)

export const useHasGrcImportWizard = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.GRC_IMPORT_WIZARD)

export const useHasIfGrantedPermissionRule = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.IF_GRANTED_PERMISSION_RULE)
